import type { ReactNode } from 'react';
import { V2Card, V2CardHeader, LoadingState, StatePanel } from '@sakhtar/design-system/react';
import type { ChartBaseStateProps } from './types.js';

export interface ChartCardProps extends ChartBaseStateProps {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode | undefined;
  readonly actions?: ReactNode | undefined;
  readonly children?: ReactNode | undefined;
  readonly className?: string | undefined;
}

/**
 * Tile-level container for charts in module hubs.
 * Hides completely (returns null) on HTTP 403 Forbidden so no empty card/header remains.
 * Renders unified loading and error states (404, 500, 503 with retry button) on non-403 errors.
 */
export function ChartCard({
  title,
  subtitle,
  actions,
  children,
  isLoading,
  loadingMessage,
  error,
  status,
  errorMessage,
  onRetry,
  retryLabel,
  className,
}: ChartCardProps): ReactNode {
  let effectiveStatus: number | null = status ?? null;
  if (effectiveStatus === null) {
    if (typeof error === 'object' && error !== null) {
      if ('status' in error && typeof (error as { status: unknown }).status === 'number') {
        effectiveStatus = (error as { status: number }).status;
      } else if (
        'statusCode' in error &&
        typeof (error as { statusCode: unknown }).statusCode === 'number'
      ) {
        effectiveStatus = (error as { statusCode: number }).statusCode;
      }
    }
  }

  // Card-level 403 hiding: do not render the card or header
  if (effectiveStatus === 403) {
    return null;
  }

  const hasError = Boolean(error || (effectiveStatus !== null && effectiveStatus >= 400));

  const statePanelProps: {
    status?: number | null;
    description?: ReactNode;
    onRetry?: () => void;
    retryLabel?: string;
  } = {};
  if (effectiveStatus !== undefined) statePanelProps.status = effectiveStatus;
  if (errorMessage !== undefined) statePanelProps.description = errorMessage;
  if (onRetry !== undefined) statePanelProps.onRetry = onRetry;
  if (retryLabel !== undefined) statePanelProps.retryLabel = retryLabel;

  const cardProps: { className?: string } = {};
  if (className !== undefined) cardProps.className = className;

  return (
    <V2Card {...cardProps}>
      <V2CardHeader title={title} subtitle={subtitle} actions={actions} />
      <div style={{ padding: '16px 20px' }}>
        {isLoading ? (
          <LoadingState label={loadingMessage ?? 'در حال بارگذاری نمودار…'} />
        ) : hasError ? (
          <StatePanel
            status={effectiveStatus}
            description={errorMessage}
            onRetry={onRetry}
            retryLabel={retryLabel}
          />
        ) : (
          children
        )}
      </div>
    </V2Card>
  );
}
